/**
 * Device Management Page
 *
 * Admin page for managing device registrations and approvals.
 * Requires 'manage_devices' permission or admin/master role.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Button,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  CircularProgress,
  Tooltip,
  Badge,
  Grid,
} from '@mui/material';
import {
  Check as ApproveIcon,
  Block as DeactivateIcon,
  DeleteForever as RevokeIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  PhoneAndroid as DeviceIcon,
  CheckCircle as ActiveIcon,
  HourglassEmpty as PendingIcon,
  Cancel as InactiveIcon,
  Warning as RevokedIcon,
} from '@mui/icons-material';
import { format, formatDistanceToNow } from 'date-fns';
import { devicesApi, Device } from '../../services/devicesApi';

type DeviceStatus = 'pending' | 'active' | 'inactive' | 'revoked';

const statusConfig: Record<DeviceStatus, { color: 'warning' | 'success' | 'default' | 'error'; icon: React.ReactNode; label: string }> = {
  pending: { color: 'warning', icon: <PendingIcon />, label: 'Pending' },
  active: { color: 'success', icon: <ActiveIcon />, label: 'Active' },
  inactive: { color: 'default', icon: <InactiveIcon />, label: 'Inactive' },
  revoked: { color: 'error', icon: <RevokedIcon />, label: 'Revoked' },
};

export function DeviceManagementPage() {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [actionDialog, setActionDialog] = useState<'approve' | 'deactivate' | 'revoke' | 'delete' | null>(null);
  const [revokeReason, setRevokeReason] = useState('');
  const [deactivateReason, setDeactivateReason] = useState('');

  // Fetch devices
  const { data: devices, isLoading, error, refetch } = useQuery({
    queryKey: ['devices', statusFilter],
    queryFn: () => devicesApi.getAllDevices({ status: statusFilter || undefined }),
  });

  // Fetch stats
  const { data: stats } = useQuery({
    queryKey: ['device-stats'],
    queryFn: () => devicesApi.getStats(),
  });

  // Mutations
  const approveMutation = useMutation({
    mutationFn: (deviceId: string) => devicesApi.approveDevice(deviceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] });
      queryClient.invalidateQueries({ queryKey: ['device-stats'] });
      closeDialog();
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: ({ deviceId, reason }: { deviceId: string; reason?: string }) =>
      devicesApi.deactivateDevice(deviceId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] });
      queryClient.invalidateQueries({ queryKey: ['device-stats'] });
      closeDialog();
    },
  });

  const reactivateMutation = useMutation({
    mutationFn: (deviceId: string) => devicesApi.reactivateDevice(deviceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] });
      queryClient.invalidateQueries({ queryKey: ['device-stats'] });
    },
  });

  const revokeMutation = useMutation({
    mutationFn: ({ deviceId, reason }: { deviceId: string; reason: string }) =>
      devicesApi.revokeDevice(deviceId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] });
      queryClient.invalidateQueries({ queryKey: ['device-stats'] });
      closeDialog();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (deviceId: string) => devicesApi.deleteDevice(deviceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] });
      queryClient.invalidateQueries({ queryKey: ['device-stats'] });
      closeDialog();
    },
  });

  const closeDialog = () => {
    setActionDialog(null);
    setSelectedDevice(null);
    setRevokeReason('');
    setDeactivateReason('');
  };

  const handleAction = (device: Device, action: 'approve' | 'deactivate' | 'revoke' | 'delete') => {
    setSelectedDevice(device);
    setActionDialog(action);
  };

  const confirmAction = () => {
    if (!selectedDevice) return;

    switch (actionDialog) {
      case 'approve':
        approveMutation.mutate(selectedDevice._id);
        break;
      case 'deactivate':
        deactivateMutation.mutate({ deviceId: selectedDevice._id, reason: deactivateReason });
        break;
      case 'revoke':
        if (revokeReason.trim()) {
          revokeMutation.mutate({ deviceId: selectedDevice._id, reason: revokeReason });
        }
        break;
      case 'delete':
        deleteMutation.mutate(selectedDevice._id);
        break;
    }
  };

  const getUserName = (device: Device) => {
    if (typeof device.userId === 'object') {
      return device.userId.name || device.userId.email || 'Unknown';
    }
    return 'Unknown';
  };

  const getUserEmail = (device: Device) => {
    if (typeof device.userId === 'object') {
      return device.userId.email || '';
    }
    return '';
  };

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Failed to load devices. Please try again.
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <DeviceIcon fontSize="large" color="primary" />
          <Typography variant="h4">Device Management</Typography>
        </Box>
        <IconButton onClick={() => refetch()} title="Refresh">
          <RefreshIcon />
        </IconButton>
      </Box>

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={2} mb={3}>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Badge badgeContent={stats.pending} color="warning">
                  <PendingIcon fontSize="large" color="warning" />
                </Badge>
                <Typography variant="h6" mt={1}>{stats.pending}</Typography>
                <Typography variant="body2" color="text.secondary">Pending</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <ActiveIcon fontSize="large" color="success" />
                <Typography variant="h6" mt={1}>{stats.active}</Typography>
                <Typography variant="body2" color="text.secondary">Active</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <InactiveIcon fontSize="large" color="disabled" />
                <Typography variant="h6" mt={1}>{stats.inactive}</Typography>
                <Typography variant="body2" color="text.secondary">Inactive</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <RevokedIcon fontSize="large" color="error" />
                <Typography variant="h6" mt={1}>{stats.revoked}</Typography>
                <Typography variant="body2" color="text.secondary">Revoked</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Box mb={2}>
        <TextField
          select
          label="Filter by Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          size="small"
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">All Devices</MenuItem>
          <MenuItem value="pending">Pending</MenuItem>
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="inactive">Inactive</MenuItem>
          <MenuItem value="revoked">Revoked</MenuItem>
        </TextField>
      </Box>

      {/* Devices Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Device</TableCell>
              <TableCell>Registered Info</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Last Used</TableCell>
              <TableCell>Registered</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : devices?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No devices found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              devices?.map((device) => (
                <TableRow key={device._id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {getUserName(device)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {getUserEmail(device)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{device.deviceName}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {device.browser} / {device.os}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{device.registeredName}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {device.registeredPhone}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={statusConfig[device.status].icon as React.ReactElement}
                      label={statusConfig[device.status].label}
                      color={statusConfig[device.status].color}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDistanceToNow(new Date(device.lastUsedAt), { addSuffix: true })}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {format(new Date(device.createdAt), 'MMM d, yyyy')}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Box display="flex" justifyContent="flex-end" gap={0.5}>
                      {device.status === 'pending' && (
                        <Tooltip title="Approve">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleAction(device, 'approve')}
                          >
                            <ApproveIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {device.status === 'active' && (
                        <Tooltip title="Deactivate">
                          <IconButton
                            size="small"
                            color="default"
                            onClick={() => handleAction(device, 'deactivate')}
                          >
                            <DeactivateIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {device.status === 'inactive' && (
                        <Tooltip title="Reactivate">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => reactivateMutation.mutate(device._id)}
                          >
                            <ApproveIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {device.status !== 'revoked' && (
                        <Tooltip title="Revoke Permanently">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleAction(device, 'revoke')}
                          >
                            <RevokeIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="default"
                          onClick={() => handleAction(device, 'delete')}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Action Dialogs */}
      <Dialog open={actionDialog === 'approve'} onClose={closeDialog}>
        <DialogTitle>Approve Device</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to approve this device for {selectedDevice?.registeredName}?
            They will be able to log in from this device.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button
            onClick={confirmAction}
            variant="contained"
            color="success"
            disabled={approveMutation.isPending}
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={actionDialog === 'deactivate'} onClose={closeDialog}>
        <DialogTitle>Deactivate Device</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Deactivating this device will prevent {selectedDevice?.registeredName} from logging in
            from this device. You can reactivate it later if needed.
          </DialogContentText>
          <TextField
            fullWidth
            label="Reason (optional)"
            value={deactivateReason}
            onChange={(e) => setDeactivateReason(e.target.value)}
            margin="normal"
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button
            onClick={confirmAction}
            variant="contained"
            color="warning"
            disabled={deactivateMutation.isPending}
          >
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={actionDialog === 'revoke'} onClose={closeDialog}>
        <DialogTitle>Revoke Device Permanently</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Revoking a device is permanent and cannot be undone. The user will need to register
            a new device to log in. This should only be used for security concerns.
          </DialogContentText>
          <TextField
            fullWidth
            label="Reason (required)"
            value={revokeReason}
            onChange={(e) => setRevokeReason(e.target.value)}
            margin="normal"
            multiline
            rows={2}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button
            onClick={confirmAction}
            variant="contained"
            color="error"
            disabled={revokeMutation.isPending || !revokeReason.trim()}
          >
            Revoke Permanently
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={actionDialog === 'delete'} onClose={closeDialog}>
        <DialogTitle>Delete Device</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this device record? This action cannot be undone.
            The user will need to register the device again if they try to log in from it.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button
            onClick={confirmAction}
            variant="contained"
            color="error"
            disabled={deleteMutation.isPending}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
